import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { Autocomplete, Circle, GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import SearchIcon from '@material-ui/icons/Search';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PeopleIcon from '@material-ui/icons/People';
import StarIcon from '@material-ui/icons/Star';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import TuneIcon from '@material-ui/icons/Tune';
import MapIcon from '@material-ui/icons/Map';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import HotelIcon from '@material-ui/icons/Hotel';
import LocalDiningIcon from '@material-ui/icons/LocalDining';
import LocationPlatformHeader from './LocationPlatformHeader';
import {
  GOOGLE_MAPS_API_KEY,
  buildRegionLabel,
  formatGuests,
  formatRating,
  getHeroImage,
  getLocationCity,
  getLocationPosition,
  getLocationSlug,
} from './locationPlatformUtils';

const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at 12% 8%, rgba(218, 151, 148, 0.22), transparent 28vw),
    linear-gradient(180deg, #fffaf6 0%, #f6eee9 54%, #fffaf6 100%);
  color: #342a26;
  font-family: Quicksand, sans-serif;
`;

const Hero = styled.section`
  max-width: 1180px;
  min-height: 430px;
  margin: 24px auto 0;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  border: 1px solid rgba(224, 207, 198, 0.84);
  border-radius: 22px;
  background-image: linear-gradient(180deg, rgba(37, 29, 25, 0.08), rgba(37, 29, 25, 0.62)), url('/assets/location-search/background.jpg');
  background-position: center;
  background-size: cover;
  box-shadow: 0 24px 70px rgba(72, 49, 38, 0.15);

  @media (max-width: 1220px) {
    margin: 18px 18px 0;
  }
`;

const HeroInner = styled.div`
  width: 100%;
  padding: 70px 28px 38px;
  color: #ffffff;

  h1 {
    max-width: 760px;
    margin: 0 0 12px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 54px;
    font-weight: 400;
    line-height: 1.05;
    letter-spacing: 0;
  }

  p {
    max-width: 640px;
    margin: 0;
    font-size: 18px;
    line-height: 1.55;
  }

  @media (max-width: 720px) {
    padding: 54px 18px 26px;

    h1 {
      font-size: 36px;
    }
  }
`;

const SearchPanel = styled.form`
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 150px 140px 150px 56px;
  gap: 10px;
  max-width: 900px;
  margin-top: 28px;

  input,
  select,
  button {
    height: 54px;
    border: 1px solid rgba(234, 219, 212, 0.9);
    border-radius: 12px;
    font-family: Quicksand, sans-serif;
    font-size: 15px;
  }

  input,
  select {
    padding: 0 14px;
    background: rgba(255, 250, 246, 0.94);
    color: #342a26;
  }

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #9f555d;
    color: #ffffff;
    cursor: pointer;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const SearchField = styled.div`
  position: relative;
`;

const TrustBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  max-width: 1180px;
  margin: 18px auto 0;
  padding: 0 28px;

  span {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #604b44;
    font-size: 13px;
    font-weight: 800;
  }

  svg {
    color: #9f555d;
  }

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Content = styled.main`
  max-width: 1180px;
  margin: 0 auto;
  padding: 28px 28px 64px;

  @media (max-width: 720px) {
    padding: 24px 18px 46px;
  }
`;

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 30px;
    font-weight: 400;
    letter-spacing: 0;
  }

  p {
    margin: 6px 0 0;
    color: #5f4c45;
    font-size: 14px;
    line-height: 1.5;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ViewToggle = styled.div`
  display: inline-flex;
  padding: 4px;
  background: #fffaf6;
  border: 1px solid #eadbd4;
  border-radius: 14px;

  button {
    min-height: 42px;
    padding: 0 13px;
    border: 0;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: transparent;
    color: #4d3d37;
    cursor: pointer;
    font-family: Quicksand, sans-serif;
    font-weight: 800;
  }

  button.active {
    background: #9f555d;
    color: #ffffff;
  }
`;

const FilterPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(224, 207, 198, 0.88);
  border-radius: 18px;
  box-shadow: 0 18px 48px rgba(79, 56, 44, 0.07);

  label {
    display: grid;
    gap: 7px;
    color: #5c4740;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  select {
    height: 44px;
    padding: 0 12px;
    border: 1px solid #eadbd4;
    border-radius: 12px;
    background: #fffdfb;
    color: #342a26;
    font-family: Quicksand, sans-serif;
  }

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const FilterActions = styled.div`
  display: flex;
  align-items: end;

  button {
    width: 100%;
    height: 44px;
    border: 1px solid #d9b5b5;
    border-radius: 12px;
    background: #fff7f4;
    color: #733f45;
    cursor: pointer;
    font-family: Quicksand, sans-serif;
    font-weight: 800;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  display: flex;
  min-height: 100%;
  flex-direction: column;
  overflow: hidden;
  color: inherit;
  text-decoration: none;
  background: #fffdfb;
  border: 1px solid #eadbd4;
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(79, 56, 44, 0.08);
`;

const CardImageWrap = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Badge = styled.span`
  position: absolute;
  left: 12px;
  top: 12px;
  padding: 7px 10px;
  background: #9f555d;
  border-radius: 999px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
`;

const CardBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  padding: 16px;

  h3 {
    margin: 0;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 23px;
    font-weight: 400;
    line-height: 1.18;
    letter-spacing: 0;
  }

  p {
    margin: 0;
    color: #5a4740;
    font-size: 14px;
    line-height: 1.55;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: #4d3d37;
  font-size: 13px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 30px;
    padding: 0 9px;
    background: #f7e9e5;
    border-radius: 999px;
    font-weight: 800;
  }

  svg {
    width: 16px;
    height: 16px;
    color: #9f555d;
  }
`;

const MapLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 370px;
  gap: 18px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const MapBox = styled.div`
  overflow: hidden;
  min-height: 620px;
  border: 1px solid #eadbd4;
  border-radius: 18px;
  background: #f4ede8;
  box-shadow: 0 18px 48px rgba(79, 56, 44, 0.08);

  @media (max-width: 720px) {
    min-height: 430px;
  }
`;

const MapList = styled.div`
  display: grid;
  gap: 12px;
  align-content: start;
`;

const MiniCard = styled(Link)`
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr);
  gap: 12px;
  padding: 10px;
  color: inherit;
  text-decoration: none;
  background: #fffdfb;
  border: 1px solid #eadbd4;
  border-radius: 14px;

  img {
    width: 104px;
    height: 86px;
    object-fit: cover;
    border-radius: 10px;
  }

  h3 {
    margin: 0 0 6px;
    font-size: 16px;
    line-height: 1.2;
  }

  p {
    margin: 0;
    color: #5a4740;
    font-size: 13px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 28px;

  button {
    min-width: 42px;
    height: 40px;
    border: 1px solid #d8cbc4;
    border-radius: 10px;
    background: ${({ active }) => (active ? '#9f555d' : '#fffdfb')};
    color: ${({ active }) => (active ? '#ffffff' : '#3d332f')};
    cursor: pointer;
  }
`;

const Loading = styled.div`
  padding: 42px 0;
  color: #6b5149;
  font-weight: 800;
`;

const markerIcon = {
  url:
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="56" viewBox="0 0 48 56"><path fill="#a85f66" stroke="#fff" stroke-width="3" d="M24 3C13.5 3 5 11.5 5 22c0 14.2 19 31 19 31s19-16.8 19-31C43 11.5 34.5 3 24 3Z"/><path fill="#fff7f4" d="M24 29.5A7.5 7.5 0 1 0 24 14a7.5 7.5 0 0 0 0 15.5Z"/><path fill="#a85f66" d="M24 26.4c-2.5-2.1-5.1-4.2-5.1-7 0-1.8 1.4-3.2 3.2-3.2 1 0 1.8.4 2.4 1.1.6-.7 1.4-1.1 2.4-1.1 1.8 0 3.2 1.4 3.2 3.2 0 2.8-2.6 4.9-5.1 7l-.5.4-.5-.4Z"/></svg>'
    ),
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: [
    { elementType: 'geometry', stylers: [{ color: '#f4ede8' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#5c4a43' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#fffaf6' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d7c9c0' }] },
  ],
};

const fallbackImage = '/assets/location-search/background.jpg';
const googleMapsLibraries = ['places'];

const getSummary = location =>
  location.editorialIntro || location.seo?.metaDescription || location.description || 'Entdeckt Bilder, Bewertungen und Details für eure Hochzeitsplanung.';

const getAddressComponent = (place, type) => {
  const component = (place.address_components || []).find(item => item.types.includes(type));
  return component ? component.long_name : '';
};

const getZoomForRadius = radiusKm => {
  const radius = Number.parseInt(radiusKm);
  if (radius <= 15) return 11;
  if (radius <= 30) return 10;
  if (radius <= 75) return 8;
  return 7;
};

const LocationSearchPage = () => {
  const { region } = useParams();
  const regionLabel = buildRegionLabel(region);
  const [searchInput, setSearchInput] = useState(region ? regionLabel : '');
  const [appliedSearch, setAppliedSearch] = useState({ q: region ? regionLabel : '' });
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [radiusKm, setRadiusKm] = useState('30');
  const [numberOfGuests, setNumberOfGuests] = useState('');
  const [onlyWithImages, setOnlyWithImages] = useState('true');
  const [locationType, setLocationType] = useState('');
  const [priceSegment, setPriceSegment] = useState('');
  const [accommodationOnly, setAccommodationOnly] = useState('');
  const [minRating, setMinRating] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [requestedPage, setRequestedPage] = useState(0);
  const [result, setResult] = useState({ locations: [], pagination: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initialQuery = region ? regionLabel : '';
    setSearchInput(initialQuery);
    setAppliedSearch({ q: initialQuery });
    setSelectedPlace(null);
    setRequestedPage(0);
  }, [region, regionLabel]);

  useEffect(() => {
    const loadLocations = async () => {
      setLoading(true);
      const response = await axios.get('location-read/', {
        params: {
          q: appliedSearch.lat ? undefined : appliedSearch.q || undefined,
          searchMode: appliedSearch.lat ? 'DESTINATION' : undefined,
          lat: appliedSearch.lat || undefined,
          lng: appliedSearch.lng || undefined,
          distance: appliedSearch.lat ? radiusKm : undefined,
          numberOfGuests: numberOfGuests || undefined,
          locationType: locationType || undefined,
          priceSegment: priceSegment || undefined,
          accommodation: accommodationOnly || undefined,
          minRating: minRating || undefined,
          onlyWithImages,
          requestedPage,
          noPagination: viewMode === 'map' ? 'true' : undefined,
        },
      });
      setResult(response.data);
      setLoading(false);
    };
    loadLocations();
  }, [appliedSearch, radiusKm, numberOfGuests, locationType, priceSegment, accommodationOnly, minRating, onlyWithImages, requestedPage, viewMode]);

  const submitSearch = event => {
    event.preventDefault();
    setAppliedSearch(
      selectedPlace
        ? {
            q: selectedPlace.label,
            lat: selectedPlace.lat,
            lng: selectedPlace.lng,
          }
        : { q: searchInput.trim() }
    );
    setRequestedPage(0);
  };

  const onAutocompleteLoad = instance => {
    setAutocomplete(instance);
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;
    const rawPlace = autocomplete.getPlace();
    if (!rawPlace || !rawPlace.geometry || !rawPlace.geometry.location) return;
    const city =
      getAddressComponent(rawPlace, 'locality') ||
      getAddressComponent(rawPlace, 'postal_town') ||
      getAddressComponent(rawPlace, 'administrative_area_level_2') ||
      rawPlace.name;
    const zip = getAddressComponent(rawPlace, 'postal_code');
    const label = [zip, city].filter(Boolean).join(' ') || rawPlace.formatted_address || rawPlace.name;
    const place = {
      label,
      lat: rawPlace.geometry.location.lat(),
      lng: rawPlace.geometry.location.lng(),
    };
    setSelectedPlace(place);
    setSearchInput(label);
  };

  const onSearchInputChange = event => {
    setSearchInput(event.target.value);
    setSelectedPlace(null);
  };

  const resetFilters = () => {
    setNumberOfGuests('');
    setLocationType('');
    setPriceSegment('');
    setAccommodationOnly('');
    setMinRating('');
    setOnlyWithImages('true');
    setRequestedPage(0);
  };

  const locations = useMemo(() => result.locations || [], [result.locations]);
  const pagination = result.pagination || {};
  const pageTitle = region ? `Hochzeitslocations in ${regionLabel} finden` : 'Findet eure Traumlocation';
  const mapLocations = locations.map(location => ({ location, position: getLocationPosition(location) })).filter(item => item.position);
  const mapCenter =
    appliedSearch.lat && appliedSearch.lng
      ? { lat: appliedSearch.lat, lng: appliedSearch.lng }
      : mapLocations.length
      ? {
          lat: mapLocations.reduce((sum, item) => sum + item.position.lat, 0) / mapLocations.length,
          lng: mapLocations.reduce((sum, item) => sum + item.position.lng, 0) / mapLocations.length,
        }
      : { lat: 51.1657, lng: 10.4515 };

  return (
    <Page>
      <Helmet>
        <title>{`${pageTitle} | EureHochzeitOnline`}</title>
        <meta
          name="description"
          content={`Entdeckt Hochzeitslocations mit Bildern, Kapazitäten, Ausstattung und Bewertungen. ${region ? `Region: ${regionLabel}.` : 'Für den gesamten DACH-Raum.'}`}
        />
        <link rel="canonical" href={`${window.location.origin}${region ? `/hochzeitslocations/${region}/` : '/hochzeitslocations/'}`} />
      </Helmet>
      <LocationPlatformHeader />
      <LoadScript id="location-search-google" googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={googleMapsLibraries}>
        <Hero>
          <HeroInner>
            <h1>{pageTitle}</h1>
            <p>Tausende Locations, echte Entscheidungshilfen und Inspiration für eine Feier, die zu euch passt.</p>
            <SearchPanel onSubmit={submitSearch}>
              <SearchField>
                <Autocomplete
                  onLoad={onAutocompleteLoad}
                  onPlaceChanged={onPlaceChanged}
                  options={{
                    fields: ['name', 'formatted_address', 'address_component', 'geometry'],
                    types: ['(regions)'],
                    componentRestrictions: { country: ['de', 'at', 'ch'] },
                  }}
                >
                  <input value={searchInput} onChange={onSearchInputChange} placeholder="Ort oder PLZ eingeben" />
                </Autocomplete>
              </SearchField>
              <select value={radiusKm} onChange={event => setRadiusKm(event.target.value)} aria-label="Umkreis">
                <option value="15">15 km Umkreis</option>
                <option value="30">30 km Umkreis</option>
                <option value="50">50 km Umkreis</option>
                <option value="75">75 km Umkreis</option>
                <option value="100">100 km Umkreis</option>
              </select>
              <select value={numberOfGuests} onChange={event => setNumberOfGuests(event.target.value)}>
                <option value="">Gästezahl</option>
                <option value="40">40-80 Gäste</option>
                <option value="80">80-200 Gäste</option>
                <option value="200">ab 200 Gäste</option>
              </select>
              <select value={onlyWithImages} onChange={event => setOnlyWithImages(event.target.value)}>
                <option value="true">mit Bildern</option>
                <option value="false">alle Profile</option>
              </select>
              <button type="submit" aria-label="Locations suchen">
                <SearchIcon />
              </button>
            </SearchPanel>
          </HeroInner>
        </Hero>
      <TrustBar>
        <span>
          <FavoriteBorderIcon /> Geprüfte Inhalte
        </span>
        <span>
          <StarIcon /> Bewertungen
        </span>
        <span>
          <LocationOnIcon /> Map-Ansicht
        </span>
        <span>
          <TuneIcon /> Filter
        </span>
      </TrustBar>
      <Content>
        <Toolbar>
          <div>
            <h2>Empfohlene Locations</h2>
            <p>
              {pagination.totalEntries || locations.length || 0} Profile mit Bildern, Bewertungen und strukturierten Details
              {appliedSearch.lat ? ` im Umkreis von ${radiusKm} km.` : '.'}
            </p>
          </div>
          <ViewToggle>
            <button type="button" className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>
              <ViewModuleIcon /> Karten
            </button>
            <button type="button" className={viewMode === 'map' ? 'active' : ''} onClick={() => setViewMode('map')}>
              <MapIcon /> Karte
            </button>
          </ViewToggle>
        </Toolbar>
        <FilterPanel>
          <label>
            Stil & Ambiente
            <select value={locationType} onChange={event => setLocationType(event.target.value)}>
              <option value="">Alle Typen</option>
              <option value="restaurant">Restaurant</option>
              <option value="hotel">Hotel</option>
              <option value="palace">Schloss</option>
              <option value="outdoor">Outdoor</option>
              <option value="classic">Klassisch</option>
            </select>
          </label>
          <label>
            Budgetgefühl
            <select value={priceSegment} onChange={event => setPriceSegment(event.target.value)}>
              <option value="">Alle Budgets</option>
              <option value="mittel">Mittel</option>
              <option value="gehoben">Gehoben</option>
              <option value="premium">Premium</option>
            </select>
          </label>
          <label>
            Bewertungen
            <select value={minRating} onChange={event => setMinRating(event.target.value)}>
              <option value="">Alle Bewertungen</option>
              <option value="4">ab 4,0 Sterne</option>
              <option value="4.5">ab 4,5 Sterne</option>
            </select>
          </label>
          <label>
            Übernachtung
            <select value={accommodationOnly} onChange={event => setAccommodationOnly(event.target.value)}>
              <option value="">Alle Profile</option>
              <option value="true">mit Übernachtung</option>
            </select>
          </label>
          <FilterActions>
            <button type="button" onClick={resetFilters}>
              Filter zurücksetzen
            </button>
          </FilterActions>
        </FilterPanel>
        {loading ? (
          <Loading>Locations werden geladen</Loading>
        ) : viewMode === 'map' ? (
          <MapLayout>
            <MapBox>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter}
                zoom={appliedSearch.lat ? getZoomForRadius(radiusKm) : mapLocations.length > 1 ? 6 : 13}
                options={mapOptions}
              >
                {appliedSearch.lat && (
                  <Circle
                    center={mapCenter}
                    radius={Number.parseInt(radiusKm) * 1000}
                    options={{
                      strokeColor: '#9f555d',
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      fillColor: '#d99ca0',
                      fillOpacity: 0.14,
                    }}
                  />
                )}
                {mapLocations.map(({ location, position }) => (
                  <Marker key={location.id || getLocationSlug(location)} position={position} icon={markerIcon} title={location.title} />
                ))}
              </GoogleMap>
            </MapBox>
            <MapList>
              {locations.slice(0, 8).map(location => (
                <MiniCard key={location.id} to={`/hochzeitslocation/${getLocationSlug(location)}/`}>
                  <img
                    src={getHeroImage(location)}
                    alt={location.title}
                    onError={event => {
                      event.currentTarget.src = fallbackImage;
                    }}
                  />
                  <div>
                    <h3>{location.title}</h3>
                    <p>{formatGuests(location)}</p>
                    {location.distanceKm !== undefined && <p>{location.distanceKm.toString().replace('.', ',')} km entfernt</p>}
                    <p>{formatRating(location) || 'Bewertungen folgen'}</p>
                  </div>
                </MiniCard>
              ))}
            </MapList>
          </MapLayout>
        ) : (
          <Grid>
            {locations.map(location => (
              <Card key={location.id} to={`/hochzeitslocation/${getLocationSlug(location)}/`}>
                <CardImageWrap>
                  <img
                    src={getHeroImage(location)}
                    alt={location.title}
                    onError={event => {
                      event.currentTarget.src = fallbackImage;
                    }}
                  />
                  <Badge>Top Location</Badge>
                </CardImageWrap>
                <CardBody>
                  <h3>{location.title}</h3>
                  <Meta>
                    {(getLocationCity(location) || location.structuredProfile?.region) && (
                      <span>
                        <LocationOnIcon /> {getLocationCity(location) || location.structuredProfile?.region}
                      </span>
                    )}
                    <span>
                      <PeopleIcon /> {formatGuests(location)}
                    </span>
                    {formatRating(location) && (
                      <span>
                        <StarIcon /> {formatRating(location)}
                      </span>
                    )}
                    {location.structuredProfile?.accommodation?.available && (
                      <span>
                        <HotelIcon /> Übernachtung
                      </span>
                    )}
                    {location.structuredProfile?.catering?.type && (
                      <span>
                        <LocalDiningIcon /> Catering
                      </span>
                    )}
                  </Meta>
                  <p>{getSummary(location)}</p>
                </CardBody>
              </Card>
            ))}
          </Grid>
        )}
        {pagination.totalPages > 1 && viewMode === 'grid' && (
          <Pagination>
            {Array.from({ length: Math.min(pagination.totalPages, 8) }).map((_, index) => (
              <button key={index} type="button" active={index === requestedPage ? 1 : 0} onClick={() => setRequestedPage(index)}>
                {index + 1}
              </button>
            ))}
          </Pagination>
        )}
      </Content>
      </LoadScript>
    </Page>
  );
};

export default LocationSearchPage;
